import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@clerk/react';

const UserContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const UserProvider = ({ children }) => {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUser = useCallback(async (force = false) => {
    if (!force && userData && !error) return userData;
    
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("No authentication token available");

      const response = await fetch(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error("Failed to verify user session");

      const data = await response.json();
      setUserData(data);
      setError(null);
      return data;
    } catch (err) {
      console.error("UserContext fetch error:", err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getToken, userData, error]);

  // Initial fetch on sign in
  useEffect(() => {
    if (isLoaded && isSignedIn && !userData && !loading) {
      fetchUser();
    }
  }, [isLoaded, isSignedIn, userData, loading, fetchUser]);

  return (
    <UserContext.Provider value={{ 
      userData, 
      setUserData, 
      loading, 
      error, 
      refreshUser: () => fetchUser(true) 
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
